import { useEffect, useRef, useState } from "react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Loader2, Truck, AlertCircle, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export interface ShippingRate {
  id: string;
  carrier: string;
  service: string;
  serviceToken: string;
  amount: string;
  baseAmount: string;
  currency: string;
  estimatedDays: number | null;
  durationTerms: string | null;
}

export interface ShippingAddress {
  name?: string;
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface ShippingRateSelectorProps {
  onRateSelect: (rate: ShippingRate | null) => void;
  onAddressConfirmed?: (address: ShippingAddress | null) => void;
  selectedRate: ShippingRate | null;
  totalWeight: number;
}

const DEFAULT_PARCEL = { length: 12, width: 9, height: 4 };

type Step = "form" | "suggestion" | "rates";

export function ShippingRateSelector({
  onRateSelect,
  onAddressConfirmed,
  selectedRate,
  totalWeight,
}: ShippingRateSelectorProps) {
  const [rates, setRates] = useState<ShippingRate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>("form");
  const [address, setAddress] = useState<ShippingAddress>({
    street1: "",
    street2: "",
    city: "",
    state: "",
    zip: "",
    country: "US",
  });
  const [suggestedAddress, setSuggestedAddress] = useState<ShippingAddress | null>(null);
  const [confirmedAddress, setConfirmedAddress] = useState<ShippingAddress | null>(null);

  const readInvokeErrorBody = async (
    err: unknown,
  ): Promise<{ message?: string; code?: string } | null> => {
    try {
      const ctx = (err as { context?: Response })?.context;
      if (ctx && typeof (ctx as Response).text === "function") {
        const text = await (ctx as Response).text();
        try {
          return JSON.parse(text);
        } catch {
          return { message: text?.slice(0, 300) };
        }
      }
    } catch {
      /* ignore */
    }
    return null;
  };

  const fetchRates = async (addr: ShippingAddress) => {
    setIsLoading(true);
    setError(null);
    setRates([]);
    onRateSelect(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke("get-shipping-rates", {
        body: {
          destination: addr,
          parcels: [{ ...DEFAULT_PARCEL, weight: Math.max(totalWeight, 0.5) }],
        },
      });

      if (fnError) {
        const body = await readInvokeErrorBody(fnError);
        const message =
          body?.message ||
          (body?.code === "NO_UPS_RATES"
            ? "UPS rates are temporarily unavailable for this shipment. Please try again shortly."
            : body?.code === "INVALID_ADDRESS"
              ? "We couldn't verify this shipping address. Please review it and try again."
              : body?.code === "RATE_LIMITED"
                ? "Too many shipping requests. Please wait a moment and try again."
                : "Unable to get shipping rates. Please try again.");
        throw new Error(message);
      }

      if (data?.ok === false) {
        throw new Error(data.message || "Unable to get shipping rates.");
      }
      const list: ShippingRate[] = data?.rates || [];
      setRates(list);
      setStep("rates");
      setConfirmedAddress(addr);
      onAddressConfirmed?.(addr);
      if (list.length > 0) onRateSelect(list[0]);
    } catch (err) {
      console.error("Failed to fetch shipping rates:", err);
      setError(err instanceof Error ? err.message : "Unable to calculate shipping.");
      setStep("form");
    } finally {
      setIsLoading(false);
    }
  };

  const validateAndContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuggestedAddress(null);
    onAddressConfirmed?.(null);

    const normalized: ShippingAddress = {
      ...address,
      street1: address.street1.trim(),
      street2: address.street2?.trim() || undefined,
      city: address.city.trim(),
      state: address.state.trim().toUpperCase(),
      zip: address.zip.trim(),
      country: address.country,
    };

    try {
      const { data, error: fnError } = await supabase.functions.invoke("validate-address", {
        body: normalized,
      });

      const body = fnError ? await readInvokeErrorBody(fnError) : data;

      if (body?.ok === false) {
        setError(body.message || "This address could not be verified. Please review and try again.");
        setIsLoading(false);
        return;
      }
      if (fnError && !body) {
        throw fnError;
      }

      const payload = body as { code?: string; suggested?: ShippingAddress };
      if (payload?.code === "SUGGESTION" && payload.suggested) {
        setSuggestedAddress(payload.suggested);
        setStep("suggestion");
        setIsLoading(false);
        return;
      }

      const finalAddr: ShippingAddress = payload?.suggested || normalized;
      await fetchRates(finalAddr);
    } catch (err) {
      console.error("Address validation error:", err);
      setError("Unable to validate address right now. Please try again.");
      setIsLoading(false);
    }
  };

  const acceptSuggestion = async () => {
    if (!suggestedAddress) return;
    setAddress({
      ...suggestedAddress,
      street2: suggestedAddress.street2 || "",
    });
    await fetchRates(suggestedAddress);
  };

  const keepOriginal = async () => {
    const normalized: ShippingAddress = {
      ...address,
      street1: address.street1.trim(),
      street2: address.street2?.trim() || undefined,
      city: address.city.trim(),
      state: address.state.trim().toUpperCase(),
      zip: address.zip.trim(),
      country: address.country,
    };
    await fetchRates(normalized);
  };

  const backToForm = () => {
    setStep("form");
    setRates([]);
    setSuggestedAddress(null);
    setConfirmedAddress(null);
    onRateSelect(null);
    onAddressConfirmed?.(null);
    setError(null);
  };

  // Re-quote whenever the cart weight changes after rates were shown, so a
  // stale (lighter) rate can never be carried into checkout.
  const lastWeightRef = useRef(totalWeight);
  useEffect(() => {
    if (lastWeightRef.current === totalWeight) return;
    lastWeightRef.current = totalWeight;
    if (step === "rates" && confirmedAddress) {
      void fetchRates(confirmedAddress);
    } else {
      setRates([]);
      onRateSelect(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalWeight]);

  const formatEstimate = (rate: ShippingRate) => {
    if (rate.estimatedDays === 1) return "1 business day";
    if (rate.estimatedDays) return `${rate.estimatedDays} business days`;
    if (rate.durationTerms) return rate.durationTerms;
    return "";
  };

  if (step === "suggestion" && suggestedAddress) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <CheckCircle2 className="h-4 w-4 text-accent" />
          <span>Confirm your address</span>
        </div>
        <p className="text-xs text-muted-foreground">
          UPS suggests a slightly different version of your address. Please choose which to use.
        </p>

        <div className="grid gap-2">
          <button
            type="button"
            onClick={acceptSuggestion}
            disabled={isLoading}
            className="text-left p-3 rounded-md border-2 border-accent bg-accent/5 hover:bg-accent/10 transition-colors"
          >
            <div className="text-xs font-semibold text-accent mb-1">Recommended (verified)</div>
            <div className="text-sm">
              {suggestedAddress.street1}
              {suggestedAddress.street2 ? `, ${suggestedAddress.street2}` : ""}
            </div>
            <div className="text-sm text-muted-foreground">
              {suggestedAddress.city}, {suggestedAddress.state} {suggestedAddress.zip} ·{" "}
              {suggestedAddress.country}
            </div>
          </button>

          <button
            type="button"
            onClick={keepOriginal}
            disabled={isLoading}
            className="text-left p-3 rounded-md border hover:border-accent/50 transition-colors"
          >
            <div className="text-xs font-semibold mb-1">Use what I entered</div>
            <div className="text-sm">
              {address.street1}
              {address.street2 ? `, ${address.street2}` : ""}
            </div>
            <div className="text-sm text-muted-foreground">
              {address.city}, {address.state} {address.zip} · {address.country}
            </div>
          </button>
        </div>

        <Button
          variant="link"
          size="sm"
          className="h-auto p-0"
          onClick={backToForm}
          disabled={isLoading}
        >
          Edit address
        </Button>

        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Getting shipping rates…
          </div>
        )}
      </div>
    );
  }

  if (step === "form") {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Truck className="h-4 w-4" />
          <span>Calculate Shipping</span>
        </div>

        <form onSubmit={validateAndContinue} className="space-y-2">
          <Input
            placeholder="Street Address"
            value={address.street1}
            onChange={(e) => setAddress({ ...address, street1: e.target.value })}
            required
            className="text-sm"
          />
          <Input
            placeholder="Apt, Suite, Unit, etc. (optional)"
            value={address.street2 || ""}
            onChange={(e) => setAddress({ ...address, street2: e.target.value })}
            className="text-sm"
          />
          <div className="grid grid-cols-2 gap-2">
            <Input
              placeholder="City"
              value={address.city}
              onChange={(e) => setAddress({ ...address, city: e.target.value })}
              required
              className="text-sm"
            />
            <Input
              placeholder="State"
              value={address.state}
              onChange={(e) => setAddress({ ...address, state: e.target.value.toUpperCase() })}
              maxLength={2}
              required
              className="text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input
              placeholder="ZIP Code"
              value={address.zip}
              onChange={(e) => setAddress({ ...address, zip: e.target.value })}
              required
              className="text-sm"
            />
            <select
              value={address.country}
              onChange={(e) => setAddress({ ...address, country: e.target.value })}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <option value="US">United States</option>
              <option value="CA">Canada</option>
            </select>
          </div>

          {error && (
            <div className="flex items-start gap-2 text-destructive text-sm p-2 bg-destructive/10 rounded-md">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <Button type="submit" variant="secondary" className="w-full" size="sm" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                Verifying address…
              </>
            ) : (
              "Verify address & get rates"
            )}
          </Button>
        </form>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Calculating shipping…</span>
      </div>
    );
  }

  if (rates.length === 0) {
    return (
      <div className="text-center py-4 text-sm text-muted-foreground">
        <p>No shipping options available for this address.</p>
        <Button variant="link" className="h-auto p-0" onClick={backToForm}>
          Change address
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Truck className="h-4 w-4" />
          <span>Shipping Options</span>
        </div>
        <Button variant="link" className="h-auto p-0 text-xs" onClick={backToForm}>
          Change address
        </Button>
      </div>

      {confirmedAddress && (
        <div className="text-xs text-muted-foreground rounded-md border p-2">
          <div className="font-medium text-foreground">Shipping to</div>
          <div>
            {confirmedAddress.street1}
            {confirmedAddress.street2 ? `, ${confirmedAddress.street2}` : ""}
          </div>
          <div>
            {confirmedAddress.city}, {confirmedAddress.state} {confirmedAddress.zip} ·{" "}
            {confirmedAddress.country}
          </div>
        </div>
      )}

      <RadioGroup
        value={selectedRate?.id || ""}
        onValueChange={(value) => {
          const rate = rates.find((r) => r.id === value);
          onRateSelect(rate || null);
        }}
        className="space-y-2"
      >
        {rates.map((rate) => (
          <div
            key={rate.id}
            className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              selectedRate?.id === rate.id
                ? "border-accent bg-accent/5"
                : "border-border hover:border-accent/50"
            }`}
            onClick={() => onRateSelect(rate)}
          >
            <RadioGroupItem value={rate.id} id={rate.id} />
            <Label htmlFor={rate.id} className="flex-1 cursor-pointer">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium text-sm">
                    {rate.carrier} {rate.service}
                  </p>
                  {formatEstimate(rate) && (
                    <p className="text-xs text-muted-foreground">{formatEstimate(rate)}</p>
                  )}
                </div>
                <span className="font-semibold text-sm">${rate.amount}</span>
              </div>
            </Label>
          </div>
        ))}
      </RadioGroup>
    </div>
  );
}